import React, { memo, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCartWishlist } from '../../context/CartWishlistContext';
import { useLanguage } from '../../context/LanguageContext';
import {
  getDisplayPrice,
  getSalePercentage,
  productHasSale,
} from '../../utils/productPricing';
import { imageLooksUsable, normalizeImageUrl } from '../../utils/productImage';
import './ProductCard.css';

const formatProductCardPrice = (price) => `GEL ${Number(price || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

const ProductCard = ({ product, onAddToCart, onAddToWishlist }) => {
  const { wishlistItems, addToWishlist, removeFromWishlist, addToCart } = useCartWishlist();
  const { t } = useLanguage();
  const [failedImages, setFailedImages] = useState(() => new Set());
  const productName = product.title || product.name || t('common.product');
  const productId = product.id || product._id;
  const categoryKey = String(product.category || '').toLowerCase();
  const watchVisual = /omega|silver/i.test(`${productName} ${product.brand || ''}`)
    ? 'silver'
    : /rolex|black|submariner/i.test(`${productName} ${product.brand || ''}`)
      ? 'black'
      : 'gold';
  const orderedImages = useMemo(() => [
    ...(imageLooksUsable(product.image) ? [normalizeImageUrl(product.image)] : []),
    ...(Array.isArray(product.images) ? product.images.filter(imageLooksUsable).map(normalizeImageUrl) : []),
  ].filter((image, index, images) => images.indexOf(image) === index && !failedImages.has(image)), [product.image, product.images, failedImages]);
  const productImage = orderedImages[0] || null;
  const hoverImage = orderedImages[1] || null;
  const displayPrice = getDisplayPrice(product);
  const salePercentage = getSalePercentage(product);
  const hasSale = productHasSale(product);

  const isSaved = wishlistItems.some((item) => (item.id || item._id) === productId);

  const handleImageError = (image) => {
    setFailedImages((current) => {
      if (current.has(image)) {
        return current;
      }

      const next = new Set(current);
      next.add(image);
      return next;
    });
  };

  const handleAddToCart = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (onAddToCart) {
      onAddToCart(product);
      return;
    }

    addToCart(product);
  };

  const handleAddToWishlist = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (isSaved) {
      removeFromWishlist(productId);
      return;
    }

    if (onAddToWishlist) {
      onAddToWishlist(product);
      return;
    }

    addToWishlist(product);
  };

  return (
    <Link to={`/products/${productId}`} className={`product-card ${hoverImage ? 'product-card--has-hover' : ''}`}>
      <div className="product-card__image-wrap">
        {productImage ? (
          <>
            <img
              className="product-card__image product-card__image--primary"
              src={productImage}
              alt={productName}
              loading="lazy"
              decoding="async"
              onError={() => handleImageError(productImage)}
            />
            {hoverImage && (
              <img
                className="product-card__image product-card__image--hover"
                src={hoverImage}
                alt=""
                loading="lazy"
                decoding="async"
                onError={() => handleImageError(hoverImage)}
              />
            )}
          </>
        ) : (
          <div className={`product-card__fallback product-card__fallback--${categoryKey || 'default'} product-card__fallback--watch-${watchVisual}`} aria-hidden="true">
            <span className="product-card__watch-strap product-card__watch-strap--top" />
            <span className="product-card__watch-body" />
            <span className="product-card__watch-face" />
            <span className="product-card__watch-crown" />
            <span className="product-card__watch-strap product-card__watch-strap--bottom" />
          </div>
        )}

        <div className="product-card__actions">
          <button type="button" className="product-card__action" onClick={handleAddToCart} aria-label={t('common.addToCart')} title={t('common.addToCart')}>
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
              <path d="M2 3h2l2.5 11h10.5l2-7.5H7" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="8" cy="18" r="1.4" fill="currentColor" />
              <circle cx="17" cy="18" r="1.4" fill="currentColor" />
            </svg>
          </button>
          <button
            type="button"
            className="product-card__action"
            onClick={handleAddToWishlist}
            aria-label={isSaved ? t('common.removeFromWishlist') : t('common.addToWishlist')}
            title={isSaved ? t('common.removeFromWishlist') : t('common.addToWishlist')}
          >
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
              <path
                d="M11 19.5s-7.5-4.4-7.5-10.1c0-2.6 1.9-4.4 4.4-4.4 1.6 0 2.9.7 3.6 1.9.7-1.2 2-1.9 3.6-1.9 2.5 0 4.4 1.8 4.4 4.4 0 5.7-7.5 10.1-7.5 10.1Z"
                stroke="currentColor"
                strokeWidth="1.7"
                fill={isSaved ? 'currentColor' : 'none'}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>

      <div className="product-card__meta">
        <div className="product-card__body">
          <h3>{productName}</h3>
          <div className="product-card__footer">
            <div className="product-card__price-group">
              <p className="product-card__price">{formatProductCardPrice(displayPrice)}</p>
              {hasSale && (
                <div className="product-card__sale-row">
                  <span className="product-card__old-price">{formatProductCardPrice(product.price)}</span>
                  <span className="product-card__discount">-{salePercentage}%</span>
                </div>
              )}
            </div>
            <span className="product-card__buy">{t('common.buyNow')}</span>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default memo(ProductCard);
